(() => {
  if (window.homepagePerformance) {
    window.homepagePerformance.refresh();
    return;
  }

  const mobileQuery = window.matchMedia('(max-width: 767px)');
  const motionSections = new Set();
  const videoSections = new Set();

  // PERF: Pause continuous homepage motion when it cannot contribute to the visible experience.
  const motionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      entry.target.dataset.homepageMotionVisible = entry.isIntersecting ? 'true' : 'false';
      entry.target.classList.toggle('homepage-motion-paused', !entry.isIntersecting || document.hidden);
    });
  });

  // PERF: Video sources are attached only near the viewport so below-fold media does not compete with interactions.
  const videoObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.dataset.videoNearViewport = entry.isIntersecting ? 'true' : 'false';
        syncVideoSection(entry.target);
      });
    },
    {rootMargin: '300px 0px'}
  );

  const loadVideo = (video) => {
    const source = video.querySelector('source[data-src]');
    if (!source || source.hasAttribute('src')) return;

    source.src = source.dataset.src;
    video.load();
  };

  const loadAnimatedImage = (image) => {
    if (!image.hasAttribute('src') && image.dataset.src) image.src = image.dataset.src;
  };

  function syncVideoSection(section) {
    const wrapper = section.querySelector('[data-autoplay-video-wrapper]');
    const media = section.querySelector('[data-autoplay-video-primary]');
    if (!wrapper || !media) return;

    const hasMobileFallback = section.dataset.hasMobileFallback === 'true';
    const useMobileFallback = mobileQuery.matches && hasMobileFallback;
    const isNearViewport = section.dataset.videoNearViewport === 'true';

    wrapper.classList.toggle('video-background-wrapper--show-fallback', useMobileFallback);

    if (useMobileFallback || !isNearViewport || document.hidden) {
      if (media.tagName === 'VIDEO') media.pause();
      return;
    }

    if (media.tagName === 'VIDEO') {
      loadVideo(media);
      const playAttempt = media.play();
      if (playAttempt) playAttempt.catch(() => {});
    } else {
      loadAnimatedImage(media);
    }
  }

  const setupVideoSection = (section) => {
    if (videoSections.has(section)) return;

    videoSections.add(section);
    videoObserver.observe(section);
  };

  const findElements = (root, selector) => {
    const elements = Array.from(root.querySelectorAll(selector));

    if (root.matches?.(selector)) elements.unshift(root);

    return elements;
  };

  const refresh = (root = document) => {
    const scope = root.querySelectorAll ? root : document;

    findElements(scope, '[data-homepage-motion]').forEach((section) => {
      if (section.dataset.homepageMotionObserved === 'true') return;

      section.dataset.homepageMotionObserved = 'true';
      motionSections.add(section);
      motionObserver.observe(section);
    });

    findElements(scope, '[data-section-type="autoplay-video"]').forEach(setupVideoSection);
  };

  const syncAllVideos = () => {
    videoSections.forEach((section) => {
      if (section.isConnected) syncVideoSection(section);
    });
  };

  // EVENT: Keep media state aligned with responsive breakpoint and background-tab changes.
  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', syncAllVideos);
  } else {
    mobileQuery.addListener(syncAllVideos);
  }

  document.addEventListener('visibilitychange', () => {
    motionSections.forEach((section) => {
      const isVisible = section.dataset.homepageMotionVisible === 'true';
      section.classList.toggle('homepage-motion-paused', document.hidden || !isVisible);
    });
    syncAllVideos();
  });

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    findElements(event.target, '[data-section-type="autoplay-video"]').forEach((section) => {
      videoObserver.unobserve(section);
      videoSections.delete(section);
    });

    findElements(event.target, '[data-homepage-motion]').forEach((section) => {
      motionObserver.unobserve(section);
      motionSections.delete(section);
    });
  });

  window.homepagePerformance = {refresh};
  refresh();
})();
